import { useEffect, useRef, useState } from 'react';

type ScrollDirection = 'up' | 'down' | 'idle';

type UseScrollDirectionReturn = {
  scrollDirection: ScrollDirection;
  scrollY: number;
};

export const useScrollDirection = (
  threshold = 10,
  scrollableElement?: HTMLElement | null,
  enabled = true,
): UseScrollDirectionReturn => {
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('idle');
  const [scrollY, setScrollY] = useState(0);
  const prevScrollY = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setScrollDirection('idle');
      setScrollY(0);
      prevScrollY.current = 0;
      return;
    }

    const getScrollY = () => {
      if (scrollableElement) {
        return scrollableElement.scrollTop;
      }
      return window.pageYOffset || document.documentElement.scrollTop;
    };

    const handleScroll = () => {
      const currentScrollY = getScrollY();
      setScrollY(currentScrollY);

      const diff = currentScrollY - prevScrollY.current;

      if (Math.abs(diff) < threshold) {
        return;
      }

      if (currentScrollY <= 0) {
        setScrollDirection('idle');
      } else if (diff > 0) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }

      prevScrollY.current = currentScrollY;
    };

    const targetElement = scrollableElement || window;
    targetElement.addEventListener('scroll', handleScroll, { passive: true });

    const initialScrollY = getScrollY();
    setScrollY(initialScrollY);
    prevScrollY.current = initialScrollY;

    return () => {
      targetElement.removeEventListener('scroll', handleScroll);
    };
  }, [threshold, scrollableElement, enabled]);

  return { scrollDirection, scrollY };
};
